import { t as uiText } from '../../i18n';
// src/features/workspace/imageUtils.ts
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../lib/firebase';

/**
 * Base64データをBlobに変換する
 */
export const base64ToBlob = (base64Data: string): Blob => {
  // data:image/jpeg;base64,/9j/4AAQ... から base64部分のみを抽出
  const base64 = base64Data.split(',')[1];
  const mimeType = base64Data.split(';')[0].split(':')[1];
  
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
};

/**
 * 画像ファイルの拡張子を取得する
 */
export const getImageExtension = (mimeType: string): string => {
  switch (mimeType) {
    case 'image/jpeg':
      return 'jpg';
    case 'image/png':
      return 'png';
    case 'image/gif':
      return 'gif';
    case 'image/webp':
      return 'webp';
    default:
      return 'jpg';
  }
};

/**
 * 一意の画像IDを生成する
 */
export const generateImageId = (): string => {
  return `img_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
};

// アップロード中のBase64画像を追跡するMap
const uploadingImages = new Map<string, Promise<string>>();

/**
 * Firebase Storageに画像をアップロードする
 */
export const uploadImageToStorage = async (
  imageData: string | File,
  workspaceId: string,
  boardId: string
): Promise<string> => {
  const uploadId = Math.random().toString(36).substring(2, 8);
  
  // Base64画像の重複アップロードチェック
  if (typeof imageData === 'string') {
    const imageKey = `${workspaceId}-${boardId}-${imageData}`;
    const existingUpload = uploadingImages.get(imageKey);
    
    if (existingUpload) {
      return await existingUpload;
    }
    
    // 新しいアップロードをMapに登録
    const uploadPromise = performImageUpload(imageData, workspaceId, boardId, uploadId);
    uploadingImages.set(imageKey, uploadPromise);
    
    try {
      const result = await uploadPromise;
      uploadingImages.delete(imageKey);
      return result;
    } catch (error) {
      uploadingImages.delete(imageKey);
      throw error;
    }
  }
  
  // Fileオブジェクトの場合は従来通り
  return await performImageUpload(imageData, workspaceId, boardId, uploadId);
};

/**
 * 実際のアップロード処理を行う内部関数
 */
const performImageUpload = async (
  imageData: string | File,
  workspaceId: string,
  boardId: string,
  uploadId: string
): Promise<string> => {
  let blob: Blob;
  let fileName: string;

  if (typeof imageData === 'string') {
    // Base64データの場合
    blob = base64ToBlob(imageData);
    const mimeType = imageData.split(';')[0].split(':')[1];
    const extension = getImageExtension(mimeType);
    const imageId = generateImageId();
    fileName = `${imageId}.${extension}`;
  } else {
    // Fileオブジェクトの場合
    blob = imageData;
    const extension = imageData.name.split('.').pop() || 'jpg';
    const imageId = generateImageId();
    fileName = `${imageId}.${extension}`;
  }

  // Storage パス: {workspaceId}/images/{boardId}/{fileName}
  const imagePath = `${workspaceId}/images/${boardId}/${fileName}`;
  const storageRef = ref(storage, imagePath);

  try {
    // アップロード実行
    const snapshot = await uploadBytes(storageRef, blob);

    // ダウンロードURL取得
    const downloadURL = await getDownloadURL(snapshot.ref);

    return downloadURL;
  } catch (error) {
    console.error(`❌ [${uploadId}] 画像アップロードエラー:`, error);
    throw new Error(uiText("Failed to upload the image: {{error}}", { error: error }));
  }
};

/**
 * Base64画像データかどうかを判定する
 */
export const isBase64Image = (src: string): boolean => {
  return src.startsWith('data:image/');
};

/**
 * Storage URLかどうかを判定する
 */
export const isStorageURL = (src: string): boolean => {
  return src.includes('firebasestorage.googleapis.com') || src.includes('storage.googleapis.com');
};

/**
 * 画像最適化のオプション
 */
export interface ImageOptimizationOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  format?: 'jpeg' | 'png' | 'webp';
}

/**
 * Canvasを使用して画像を最適化する
 */
export const optimizeImage = async (
  file: File,
  options: ImageOptimizationOptions = {}
): Promise<File> => {
  const {
    maxWidth = 1920,
    maxHeight = 1080,
    quality = 0.8,
    format = 'jpeg'
  } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Canvas 2D context not supported'));
      return;
    }

    img.onload = () => {
      // 元のサイズ
      let { width, height } = img;

      // アスペクト比を保持してリサイズ
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }

      // キャンバスにサイズを設定
      canvas.width = width;
      canvas.height = height;

      // 画像を描画
      ctx.drawImage(img, 0, 0, width, height);

      // 最適化された画像をBlobとして取得
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error(uiText("Failed to optimize the image")));
            return;
          }

          // Fileオブジェクトを作成
          const optimizedFile = new File(
            [blob],
            file.name.replace(/\.[^/.]+$/, `.${format === 'jpeg' ? 'jpg' : format}`),
            {
              type: `image/${format}`,
              lastModified: Date.now()
            }
          );

          resolve(optimizedFile);
        },
        `image/${format}`,
        quality
      );
    };

    img.onerror = () => {
      reject(new Error(uiText("Failed to load the image")));
    };

    // ファイルをData URLとして読み込み
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };
    reader.onerror = () => {
      reject(new Error(uiText("Failed to load file")));
    };
    reader.readAsDataURL(file);
  });
};

/**
 * アップロード用の画像最適化（レガシー関数名）
 */
export const optimizeImageForUpload = async (
  file: File,
  options: ImageOptimizationOptions = {}
): Promise<File> => {
  return optimizeImage(file, options);
};

/**
 * Base64画像を最適化する
 */
export const optimizeBase64Image = async (
  base64Data: string,
  options: ImageOptimizationOptions = {}
): Promise<string> => {
  // Base64をBlobに変換
  const blob = base64ToBlob(base64Data);
  
  // BlobをFileに変換
  const file = new File([blob], 'image.jpg', { type: blob.type });
  
  // 最適化
  const optimizedFile = await optimizeImage(file, options);
  
  // 最適化されたファイルをBase64に変換
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = () => {
      reject(new Error(uiText("Failed to convert the optimized image to Base64")));
    };
    reader.readAsDataURL(optimizedFile);
  });
};

/**
 * Firebase Storage URLからパスを抽出する
 */
export const getStoragePathFromURL = (url: string): string | null => {
  try {
    // Firebase Storage URLのパターンをチェック
    if (!isStorageURL(url)) {
      return null;
    }

    // URLからパスを抽出
    // https://firebasestorage.googleapis.com/v0/b/bucket/o/path%2Fto%2Ffile.jpg?alt=media&token=...
    const urlObj = new URL(url);
    const pathParam = urlObj.pathname.split('/o/')[1];
    
    if (!pathParam) {
      return null;
    }

    // URLデコードしてパスを取得
    const decodedPath = decodeURIComponent(pathParam.split('?')[0]);
    return decodedPath;
  } catch (error) {
    console.error('❌ Storage URLからのパス抽出失敗:', error);
    return null;
  }
};

/**
 * 期限切れのStorage URLを新しいURLにリフレッシュする
 * @param storageURL 期限切れの可能性があるFirebase Storage URL
 * @returns 新しいダウンロードURL
 */
export const refreshStorageURL = async (storageURL: string): Promise<string | null> => {
  try {
    // Storage URLかどうかをチェック
    if (!isStorageURL(storageURL)) {
      return storageURL; // Storage URLでない場合はそのまま返す
    }

    // URLからStorage パスを抽出
    const storagePath = getStoragePathFromURL(storageURL);
    if (!storagePath) {
      console.error('❌ Storage URLからパスの抽出に失敗:', storageURL.substring(0, 50));
      return null;
    }

    // 新しいダウンロードURLを生成
    const storageRef = ref(storage, storagePath);
    const newDownloadURL = await getDownloadURL(storageRef);

    return newDownloadURL;
  } catch (error) {
    console.error('❌ Storage URLリフレッシュエラー:', {
      url: storageURL.substring(0, 50),
      error: error instanceof Error ? error.message : String(error)
    });
    return null;
  }
};

/**
 * Firebase StorageのURLから画像を削除する
 * @param storageURL Firebase StorageのダウンロードURL
 * @returns 削除成功の可否
 */
export const deleteImageFromStorage = async (storageURL: string): Promise<boolean> => {
  try {
    // Storage URLかどうかをチェック
    if (!isStorageURL(storageURL)) {
      console.warn('⚠️ Storage URLではないため削除をスキップ:', storageURL.substring(0, 50));
      return false;
    }

    // URLからStorage パスを抽出
    const storagePath = getStoragePathFromURL(storageURL);
    if (!storagePath) {
      console.error('❌ Storage URLからパスの抽出に失敗:', storageURL.substring(0, 50));
      return false;
    }

    // Storage参照を作成して削除実行
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);

    return true;
  } catch (error) {
    console.error('❌ Storage画像削除エラー:', {
      url: storageURL.substring(0, 50),
      error: error instanceof Error ? error.message : String(error)
    });
    return false;
  }
};

/**
 * 複数のStorage画像を一括削除する
 * @param storageURLs 削除対象のStorage URLの配列
 * @returns 削除成功した数
 */
export const deleteMultipleImagesFromStorage = async (storageURLs: string[]): Promise<number> => {
  let successCount = 0;

  // 並列で削除実行
  const deletePromises = storageURLs.map(async (url) => {
    const success = await deleteImageFromStorage(url);
    if (success) successCount++;
    return success;
  });

  await Promise.all(deletePromises);

  return successCount;
};

/**
 * 画像処理のエラーハンドリング
 */
export class ImageUploadError extends Error {
  constructor(message: string, public originalError?: Error) {
    super(message);
    this.name = 'ImageUploadError';
  }
}